/**
 * Event Group Configuration
 *
 * Controls which event groups are active. Each group can be toggled
 * via localStorage overrides for debugging or volume control.
 *
 * Usage:
 *   import { isGroupEnabled, setGroupEnabled } from './event-group-config.js';
 *
 *   if (isGroupEnabled('extension.library')) { ... }
 *   setGroupEnabled('extension.ui', false); // disable UI events
 */

const STORAGE_KEY = 'showlark_analytics_groups';

/**
 * Default event group definitions.
 * All groups are enabled by default.
 */
const EVENT_GROUPS = {
  'extension.connection': { enabled: true },
  'extension.library':    { enabled: true },
  'extension.generation': { enabled: true },
  'extension.playback':   { enabled: true },
  'extension.ui':         { enabled: true },
};

/**
 * Read localStorage overrides.
 * @returns {Object} Map of group name → boolean override
 */
function getOverrides() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * Check if an event group is enabled.
 * Checks localStorage override first, then falls back to defaults.
 * Unknown group names return true (don't break existing events).
 *
 * @param {string} groupName - Event group name (e.g. 'extension.library')
 * @returns {boolean}
 */
export function isGroupEnabled(groupName) {
  const overrides = getOverrides();

  // Check localStorage override first
  if (groupName in overrides) {
    return overrides[groupName];
  }

  // Check default config
  if (groupName in EVENT_GROUPS) {
    return EVENT_GROUPS[groupName].enabled;
  }

  // Unknown groups default to enabled
  return true;
}

/**
 * Set the enabled state for an event group.
 * Persists to localStorage.
 *
 * @param {string} groupName - Event group name
 * @param {boolean} enabled - Whether the group should be enabled
 */
export function setGroupEnabled(groupName, enabled) {
  const overrides = getOverrides();
  overrides[groupName] = enabled;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    // localStorage may be full or unavailable — silently ignore
  }
}

/**
 * Get all event groups with their current enabled state.
 * Merges defaults with any localStorage overrides.
 *
 * @returns {Object} Map of group name → { enabled: boolean }
 */
export function getGroups() {
  const overrides = getOverrides();
  const result = {};

  for (const [name, config] of Object.entries(EVENT_GROUPS)) {
    result[name] = {
      enabled: name in overrides ? overrides[name] : config.enabled
    };
  }

  return result;
}
